import { DatesRangeValue } from '@mantine/dates';
import { Option } from 'baseui/select';
import { Filter } from 'common/repositories/common.model';
import { toISOString } from 'common/utils/date';
import { capitalize } from 'common/utils/string';
import DatePickerInput from 'components/elements/date-picker-input';
import { MasterDatePicker, MasterInput } from 'components/elements/master';
import useFilterHandleComponent from 'hooks/filter/use-filter-handle-component';
import Select, { SelectTypeEnum } from 'legacy-modules/selects';
import React from 'react';

import { SelectComponent } from './select';
import { DateRangeEnum, afterBeforeHandler } from './utils';

interface FilterContentProps {
  filter: Filter;
  onSelected: (value: any) => void;
  value?: string;
  clearable?: boolean;
}

export default function FilterContent(props: FilterContentProps) {
  const { filter, onSelected, clearable } = props;

  const filterComponentAction = useFilterHandleComponent();
  const { isUseResponseOptions, filters } = filterComponentAction;

  const value = React.useMemo(() => {
    if (typeof props.value === 'string') {
      return props.value;
    }
    if (typeof filter.value === 'string') {
      return filter.value;
    }
    if (typeof filter.default === 'string') {
      return filter.default;
    }
    return undefined;
  }, [filter.default, filter.value, props.value]);

  const options: Option[] = (filter.options || []).map((option) => {
    const id = option.value ?? option;
    return {
      id: typeof id === 'string' ? id : `${id}`,
      label: capitalize(option.label ?? option),
      extra: option,
    };
  });

  if (filter.name.includes(DateRangeEnum.atBefore)) return null;

  if (filter.name.includes(DateRangeEnum.atAfter)) {
    const { atBeforeName, filterLabel, transformerChange, value } =
      afterBeforeHandler(filter, filters, props.value);

    return (
      <DatePickerInput
        size="xs"
        styles={{
          label: {
            fontSize: '14px',
            marginBottom: '2px',
          },
          input: {
            fontSize: '16px',
            border: '1px solid rgb(164, 167, 168)',
          },
          placeholder: {
            color: 'rgb(190, 192, 193)',
          },
        }}
        type="range"
        label={filterLabel}
        placeholder="DD-MM-YYYY - DD-MM-YYYY"
        value={value}
        onChange={(val) => {
          if (!Array.isArray(val)) return;
          const { atAfter, atBefore } = transformerChange(
            val as DatesRangeValue,
          );
          onSelected(atAfter);
          filterComponentAction.onSelected(atBeforeName)(atBefore);
        }}
        noMargin
        clearable={clearable}
      />
    );
  }

  const FilterSelect = SelectComponent(filter, isUseResponseOptions);

  if (FilterSelect) {
    return (
      <FilterSelect
        onSelected={onSelected}
        label={filter.label}
        value={value}
        noMargin
      />
    );
  }

  switch (filter.type) {
    case 'text':
      return (
        <MasterInput
          label={filter.label}
          onChange={(e) => {
            onSelected(e.target.value);
          }}
          value={value || ''}
          noMargin
        />
      );

    case 'date':
      return (
        <MasterDatePicker
          label={filter.label}
          onChange={(val) => {
            if (val) {
              onSelected(toISOString(val));
            } else {
              onSelected(undefined);
            }
          }}
          value={value ? new Date(value) : undefined}
          noMargin
        />
      );
    case 'option':
      return (
        <Select
          type={SelectTypeEnum.report}
          label={filter.label}
          onSelected={onSelected}
          placeholder={filter.label}
          value={value}
          noMargin
          options={options}
        />
      );
    default:
      return <>{filter.type}</>;
  }
}
